import { gpHome } from '../../../app';
import {
    LicenseAssignmentRequest,
    LicenseSummary,
    LicenseTypeDefinition,
    SeatUpdateRequest,
    SubscriptionSummary,
    TeamMemberInviteRequest,
    TeamMemberRoleUpdateRequest,
    TeamSummary,
    TeamUpsertRequest,
} from '../../../services/subscription-management-contract';
import teamSubscriptionsTemplate from './team-subscriptions.html';

interface TeamSubscriptionsBindings {
    teams: TeamSummary[];
    selectedTeamId: string | null;
    canEdit: boolean;
    showCreatePanel: boolean;
    showCreatePanelOnly: boolean;
    licenseTypes: LicenseTypeDefinition[];
    onCreateTeam: (locals: { request: TeamUpsertRequest }) => ng.IPromise<any>;
    onUpdateTeam: (locals: { teamId: string, request: TeamUpsertRequest }) => ng.IPromise<any>;
    onDeleteTeam: (locals: { teamId: string }) => ng.IPromise<any>;
    onInviteMember: (locals: { teamId: string, request: TeamMemberInviteRequest }) => ng.IPromise<any>;
    onRemoveMember: (locals: { teamId: string, userId: string }) => ng.IPromise<any>;
    onUpdateMemberRole: (locals: { teamId: string, userId: string, request: TeamMemberRoleUpdateRequest }) => ng.IPromise<any>;
    onAssignLicense: (locals: { request: LicenseAssignmentRequest }) => ng.IPromise<any>;
    onUnassignLicense: (locals: { licenseId: string }) => ng.IPromise<any>;
    onUpdateSubscriptionSeats: (locals: { request: SeatUpdateRequest }) => ng.IPromise<any>;
}

class TeamSubscriptionsController implements TeamSubscriptionsBindings {
    static $inject = ['$scope'];

    teams: TeamSummary[] = [];
    selectedTeamId: string | null = null;
    canEdit: boolean = true;
    showCreatePanel: boolean = true;
    showCreatePanelOnly: boolean = false;
    licenseTypes: LicenseTypeDefinition[] = [];
    onCreateTeam: (locals: { request: TeamUpsertRequest }) => ng.IPromise<any>;
    onUpdateTeam: (locals: { teamId: string, request: TeamUpsertRequest }) => ng.IPromise<any>;
    onDeleteTeam: (locals: { teamId: string }) => ng.IPromise<any>;
    onInviteMember: (locals: { teamId: string, request: TeamMemberInviteRequest }) => ng.IPromise<any>;
    onRemoveMember: (locals: { teamId: string, userId: string }) => ng.IPromise<any>;
    onUpdateMemberRole: (locals: { teamId: string, userId: string, request: TeamMemberRoleUpdateRequest }) => ng.IPromise<any>;
    onAssignLicense: (locals: { request: LicenseAssignmentRequest }) => ng.IPromise<any>;
    onUnassignLicense: (locals: { licenseId: string }) => ng.IPromise<any>;
    onUpdateSubscriptionSeats: (locals: { request: SeatUpdateRequest }) => ng.IPromise<any>;

    newTeam: TeamUpsertRequest = { name: '', description: '' };
    newMemberForms: { [teamId: string]: TeamMemberInviteRequest } = {};
    roleSelections: { [teamId: string]: { [userId: string]: string[] } } = {};
    seatAdjustments: { [subscriptionId: string]: number } = {};
    licenseAssignments: { [licenseId: string]: string } = {};
    isEditingTeam: boolean = false;
    editableTeam: TeamUpsertRequest | null = null;

    createTeam() {
        if (!this.newTeam.name) {
            return;
        }
        this.onCreateTeam({ request: this.newTeam }).then(() => {
            this.newTeam = { name: '', description: '' };
        });
    }

    saveTeam(team: TeamSummary) {
        const request: TeamUpsertRequest = {
            name: team.name,
            description: team.description,
        };
        return this.onUpdateTeam({ teamId: team.id, request });
    }

    deleteTeam(teamId: string) {
        return this.onDeleteTeam({ teamId });
    }

    invite(teamId: string) {
        const form = this.newMemberForms[teamId] || { email: '', displayName: '', roleKeys: ['member'] };
        if (!form.email) {
            return;
        }
        this.onInviteMember({ teamId, request: form }).then(() => {
            this.newMemberForms[teamId] = { email: '', displayName: '', roleKeys: ['member'] };
        });
    }

    removeMember(teamId: string, userId: string) {
        return this.onRemoveMember({ teamId, userId });
    }

    updateRole(teamId: string, memberId: string, selected: string[]) {
        const request: TeamMemberRoleUpdateRequest = { roleKeys: selected };
        return this.onUpdateMemberRole({ teamId, userId: memberId, request });
    }

    assignLicense(team: TeamSummary, license: LicenseSummary) {
        const value = this.licenseAssignments[license.id];
        if (!value) {
            return;
        }
        const request: LicenseAssignmentRequest = {
            licenseId: license.id,
            userId: value,
            teamId: team.id,
        };
        return this.onAssignLicense({ request });
    }

    unassignLicense(license: LicenseSummary) {
        return this.onUnassignLicense({ licenseId: license.id });
    }

    updateSeats(subscription: SubscriptionSummary) {
        const desiredSeats = this.seatAdjustments[subscription.id] || subscription.seatsPurchased;
        const request: SeatUpdateRequest = {
            subscriptionId: subscription.id,
            seatsPurchased: desiredSeats,
        };
        return this.onUpdateSubscriptionSeats({ request });
    }

    get selectedTeam(): TeamSummary | undefined {
        return this.teams.find(t => t.id === this.selectedTeamId);
    }

    beginEditTeam() {
        if (!this.canEdit || !this.selectedTeam) {
            return;
        }
        this.isEditingTeam = true;
        this.editableTeam = { name: this.selectedTeam.name, description: this.selectedTeam.description };
    }

    cancelEditTeam() {
        this.isEditingTeam = false;
        this.editableTeam = null;
    }

    saveEditedTeam() {
        if (!this.canEdit || !this.selectedTeam || !this.editableTeam) {
            return;
        }
        this.selectedTeam.name = this.editableTeam.name;
        this.selectedTeam.description = this.editableTeam.description;
        return this.saveTeam(this.selectedTeam).finally(() => {
            this.isEditingTeam = false;
            this.editableTeam = null;
        });
    }
}

gpHome.directive('gpTeamSubscriptions', [
    function () {
        return {
            restrict: 'E',
            scope: {
                teams: '=',
                selectedTeamId: '=',
                canEdit: '=',
                showCreatePanel: '=',
                showCreatePanelOnly: '=',
                licenseTypes: '=',
                onCreateTeam: '&',
                onUpdateTeam: '&',
                onDeleteTeam: '&',
                onInviteMember: '&',
                onRemoveMember: '&',
                onUpdateMemberRole: '&',
                onAssignLicense: '&',
                onUnassignLicense: '&',
                onUpdateSubscriptionSeats: '&',
            },
            controller: TeamSubscriptionsController,
            controllerAs: '$ctrl',
            bindToController: true,
            template: teamSubscriptionsTemplate,
        };
    }
]);
